import React, { useState, useEffect } from 'react';
import './PagePhotos.css';

// Simple photo upload form component
function PhotoUploadForm({ onUpload, onCancel, suggestedKeys = [], pageName, prefilledKey = null, photoSpecs = {} }) {
  const [formData, setFormData] = useState({
    file: null,
    key: prefilledKey || '',
    altText: '',
    caption: '',
    displayOrder: 0
  });
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  // Update form when prefilledKey changes
  useEffect(() => {
    if (prefilledKey) {
      setFormData(prev => ({ ...prev, key: prefilledKey }));
    }
  }, [prefilledKey]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, file }));
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file || !formData.key) return;

    setUploading(true);
    const result = await onUpload(formData);
    setUploading(false);

    if (result.success) {
      setFormData({ file: null, key: '', altText: '', caption: '', displayOrder: 0 });
      setPreview(null);
    }
  };

  const currentSpec = photoSpecs[formData.key];

  return (
    <form onSubmit={handleSubmit} className="photo-upload-form">
      {/* Photo Specifications Display */}
      {currentSpec && (
        <div className="alert alert-info mb-3">
          <h6><i className="fas fa-info-circle me-2"></i>{currentSpec.name}</h6>
          <p className="mb-2">{currentSpec.description}</p>
          <div className="spec-requirements">
            <small>
              <strong>Recommended:</strong> {currentSpec.dimensions} • 
              <strong> Max Size:</strong> {currentSpec.maxSize} • 
              <strong> Aspect Ratio:</strong> {currentSpec.aspectRatio}
            </small>
          </div>
        </div>
      )}

      <div className="form-group mb-3">
        <label className="form-label">Photo File</label>
        <input
          type="file"
          className="form-control"
          accept="image/*"
          onChange={handleFileChange}
          required
        />
        {preview && (
          <div className="mt-2">
            <img src={preview} alt="Preview" className="img-thumbnail" style={{maxWidth: '200px'}} />
          </div>
        )}
      </div>

      <div className="form-group mb-3">
        <label className="form-label">Photo Key</label>
        <input
          type="text"
          className="form-control"
          value={formData.key}
          onChange={(e) => setFormData(prev => ({ ...prev, key: e.target.value }))}
          placeholder="e.g. banner_slide_1"
          required
        />
        {suggestedKeys.length > 0 && (
          <small className="form-text text-muted">
            Available photo types: {suggestedKeys.join(', ')}
          </small>
        )}
      </div>

      <div className="form-group mb-3">
        <label className="form-label">Alt Text</label>
        <input
          type="text"
          className="form-control"
          value={formData.altText}
          onChange={(e) => setFormData(prev => ({ ...prev, altText: e.target.value }))}
          placeholder="Description for accessibility"
        />
      </div>

      <div className="form-group mb-3">
        <label className="form-label">Caption (Optional)</label>
        <input
          type="text"
          className="form-control"
          value={formData.caption}
          onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
          placeholder="Photo caption"
        />
      </div>

      <div className="form-group mb-3">
        <label className="form-label">Display Order</label>
        <input
          type="number"
          className="form-control"
          value={formData.displayOrder}
          onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
          min="0"
        />
      </div>

      <div className="d-flex justify-content-end gap-2">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={uploading || !formData.file || !formData.key}>
          {uploading ? 'Uploading...' : 'Upload Photo'}
        </button>
      </div>
    </form>
  );
}

function PagePhotos() {
  const [activeTab, setActiveTab] = useState('home');
  const [photos, setPhotos] = useState({
    home: [],
    about: [],
    contact: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [prefilledKey, setPrefilledKey] = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  useEffect(() => {
    fetchPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      const pages = ['home', 'about', 'contact'];
      const photoPromises = pages.map(page =>
        fetch(`${API_BASE}/page-photos/${page}`)
          .then(res => res.json())
          .then(data => ({ page, photos: data }))
      );

      const results = await Promise.all(photoPromises);
      const newPhotos = {};
      
      results.forEach(result => {
        newPhotos[result.page] = result.photos;
      });

      setPhotos(newPhotos);
      setError(null);
    } catch (err) {
      console.error('Error fetching photos:', err);
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (photoData) => {
    const formData = new FormData();
    formData.append('photo', photoData.file);
    formData.append('photo_key', photoData.key);
    formData.append('alt_text', photoData.altText);
    formData.append('caption', photoData.caption);
    formData.append('display_order', photoData.displayOrder);

    try {
      const token = localStorage.getItem('cms_token');
      const response = await fetch(`${API_BASE}/page-photos/${activeTab}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      await fetchPhotos();
      setUploadModalOpen(false);
      return { success: true };
    } catch (err) {
      console.error('Upload error:', err);
      return { success: false, error: err.message };
    }
  };

  const handlePhotoDelete = async (photoKey) => {
    if (!window.confirm(`Are you sure you want to delete this photo (${photoKey})?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('cms_token');
      const response = await fetch(`${API_BASE}/page-photos/${activeTab}/${photoKey}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Delete failed');
      }

      await fetchPhotos();
    } catch (err) {
      console.error('Delete error:', err);
      setError(`Failed to delete photo: ${err.message}`);
    }
  };

  const pageLabels = {
    home: 'Home Page',
    about: 'About Page',
    contact: 'Contact Page'
  };

  const photoSpecs = {
    home: {
      'banner_slide_1': {
        name: 'Banner Slide 1',
        dimensions: '1920x1080px',
        description: 'Main banner carousel image',
        maxSize: '3MB',
        aspectRatio: '16:9'
      },
      'banner_slide_2': {
        name: 'Banner Slide 2', 
        dimensions: '1920x1080px',
        description: 'Second banner carousel image',
        maxSize: '3MB',
        aspectRatio: '16:9'
      },
      'banner_slide_3': {
        name: 'Banner Slide 3',
        dimensions: '1920x1080px', 
        description: 'Third banner carousel image',
        maxSize: '3MB',
        aspectRatio: '16:9'
      },
      'story_image_1': {
        name: 'Story Image 1',
        dimensions: '600x400px',
        description: 'Restaurant story section image',
        maxSize: '2MB',
        aspectRatio: '3:2'
      },
      'story_image_2': {
        name: 'Story Image 2',
        dimensions: '600x400px',
        description: 'Second story section image', 
        maxSize: '2MB',
        aspectRatio: '3:2'
      },
      'menu_image_1': {
        name: 'Menu Image 1',
        dimensions: '300x300px',
        description: 'First menu dish image - used in menu section',
        maxSize: '1MB',
        aspectRatio: '1:1'
      },
      'menu_image_2': {
        name: 'Menu Image 2',
        dimensions: '300x300px',
        description: 'Second menu dish image - used in menu section',
        maxSize: '1MB',
        aspectRatio: '1:1'
      },
      'menu_image_3': {
        name: 'Menu Image 3',
        dimensions: '300x300px',
        description: 'Third menu dish image - used in menu section',
        maxSize: '1MB',
        aspectRatio: '1:1'
      },
      'menu_image_4': {
        name: 'Menu Image 4',
        dimensions: '300x300px',
        description: 'Fourth menu dish image - used in menu section',
        maxSize: '1MB',
        aspectRatio: '1:1'
      },
      'menu_image_5': {
        name: 'Menu Image 5',
        dimensions: '300x300px',
        description: 'Fifth menu dish image - used in menu section',
        maxSize: '1MB',
        aspectRatio: '1:1'
      },
      'menu_image_6': {
        name: 'Menu Image 6',
        dimensions: '300x300px',
        description: 'Sixth menu dish image - used in menu section',
        maxSize: '1MB',
        aspectRatio: '1:1'
      }
    },
    about: {
      'banner_image': {
        name: 'About Banner',
        dimensions: '1920x600px',
        description: 'About page header banner',
        maxSize: '2MB',
        aspectRatio: '16:5'
      },
      'about_image_1': {
        name: 'About Image 1',
        dimensions: '800x600px',
        description: 'Main about section image',
        maxSize: '2MB', 
        aspectRatio: '4:3'
      },
      'about_image_2': {
        name: 'About Image 2',
        dimensions: '800x600px',
        description: 'Secondary about section image',
        maxSize: '2MB',
        aspectRatio: '4:3'
      }
    },
    contact: {
      'banner_image': {
        name: 'Contact Banner',
        dimensions: '1920x600px',
        description: 'Contact page header banner',
        maxSize: '2MB',
        aspectRatio: '16:5'
      }
    }
  };

  const photoKeyHints = {
    home: Object.keys(photoSpecs.home),
    about: Object.keys(photoSpecs.about),
    contact: Object.keys(photoSpecs.contact)
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p className="mt-3">Loading page photos...</p>
      </div>
    );
  }

  return (
    <div className="page-photos-management">
      <div className="page-header mb-4">
        <h2 className="page-title">
          <i className="fas fa-images me-3"></i>
          Page Photos Management
        </h2>
        <p className="page-description text-muted">
          Manage all photos displayed across your website pages
        </p>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Page Tabs */}
      <div className="nav-tabs-wrapper mb-4">
        <ul className="nav nav-tabs">
          {Object.keys(pageLabels).map(page => (
            <li className="nav-item" key={page}>
              <button
                className={`nav-link ${activeTab === page ? 'active' : ''}`}
                onClick={() => setActiveTab(page)}
              >
                <i className={`fas ${
                  page === 'home' ? 'fa-home' : 
                  page === 'about' ? 'fa-info-circle' : 
                  'fa-envelope'
                } me-2`}></i>
                {pageLabels[page]}
                {photos[page] && (
                  <span className="badge bg-primary ms-2">{photos[page].length}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Active Tab Content */}
      <div className="tab-content">
        <div className="tab-pane active">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">
              <i className={`fas ${
                activeTab === 'home' ? 'fa-home' : 
                activeTab === 'about' ? 'fa-info-circle' : 
                'fa-envelope'
              } me-2`}></i>
              {pageLabels[activeTab]} Photos
            </h4>
            <button
              className="btn btn-primary"
              onClick={() => setUploadModalOpen(true)}
            >
              <i className="fas fa-plus me-2"></i>
              Add New Photo
            </button>
          </div>

          {/* Photo Specifications Guide */}
          <div className="photo-specs-guide mb-4">
            <h5>Photo Specifications for {pageLabels[activeTab]}</h5>
            <div className="specs-grid">
              {Object.entries(photoSpecs[activeTab] || {}).map(([key, spec]) => {
                const existingPhoto = photos[activeTab]?.find(p => p.photo_key === key);
                return (
                  <div key={key} className={`spec-card ${existingPhoto ? 'has-photo' : 'needs-photo'}`}>
                    <div className="spec-header">
                      <h6 className="spec-name">
                        {spec.name}
                        {existingPhoto ? (
                          <span className="badge bg-success ms-2">✓ Uploaded</span>
                        ) : (
                          <span className="badge bg-warning ms-2">⚠ Missing</span>
                        )}
                      </h6>
                    </div>
                    <div className="spec-details">
                      <p className="spec-description">{spec.description}</p>
                      <div className="spec-requirements">
                        <span className="spec-item"><strong>Dimensions:</strong> {spec.dimensions}</span>
                        <span className="spec-item"><strong>Max Size:</strong> {spec.maxSize}</span>
                        <span className="spec-item"><strong>Aspect Ratio:</strong> {spec.aspectRatio}</span>
                      </div>
                      {existingPhoto && (
                        <div className="current-photo-preview">
                          <img 
                            src={existingPhoto.photo_url}
                            alt={existingPhoto.alt_text || ''}
                            className="mini-preview"
                          />
                        </div>
                      )}
                      <div className="spec-actions">
                        {existingPhoto ? (
                          <>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => {
                                setUploadModalOpen(true);
                                setPrefilledKey(existingPhoto.photo_key);
                              }}
                            >
                              <i className="fas fa-edit me-1"></i>Replace
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handlePhotoDelete(existingPhoto.photo_key)}
                            >
                              <i className="fas fa-trash me-1"></i>Remove
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                              setUploadModalOpen(true);
                              // Pre-fill the photo key in the upload form
                              setPrefilledKey(key);
                            }}
                          >
                            <i className="fas fa-plus me-1"></i>Upload Photo
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Existing Photos Grid (for any extra photos not in specs) */}
          {(photos[activeTab] || []).filter(photo => 
            !Object.keys(photoSpecs[activeTab] || {}).includes(photo.photo_key)
          ).length > 0 && (
            <>
              <h5>Additional Photos</h5>
              <div className="photo-grid">
                {(photos[activeTab] || [])
                  .filter(photo => !Object.keys(photoSpecs[activeTab] || {}).includes(photo.photo_key))
                  .sort((a, b) => a.display_order - b.display_order)
                  .map(photo => (
                  <div key={photo.id} className="photo-card">
                    <div className="photo-preview">
                      <img 
                        src={photo.photo_url}
                        alt={photo.alt_text || ''}
                        onError={(e) => {
                          e.target.src = '/placeholder-image.jpg';
                        }}
                      />
                      <div className="photo-overlay">
                        <button
                          className="btn btn-sm btn-outline-light me-2"
                          onClick={() => {
                            setUploadModalOpen(true);
                            setPrefilledKey(photo.photo_key);
                          }}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handlePhotoDelete(photo.photo_key)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <div className="photo-info">
                      <h6 className="photo-key">{photo.photo_key}</h6>
                      {photo.alt_text && (
                        <p className="photo-alt text-muted mb-1">
                          <small><strong>Alt:</strong> {photo.alt_text}</small>
                        </p>
                      )}
                      {photo.caption && (
                        <p className="photo-caption text-muted mb-1">
                          <small><strong>Caption:</strong> {photo.caption}</small>
                        </p>
                      )}
                      <p className="photo-order text-muted mb-0">
                        <small><strong>Order:</strong> {photo.display_order}</small>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Empty state is shown when no specified photos and no additional photos */}
      {(!photos[activeTab] || photos[activeTab].length === 0) && (
        <div className="tab-content">
          <div className="tab-pane active">
            <div className="empty-state text-center py-5">
              <i className="fas fa-images fa-4x text-muted mb-3"></i>
              <h5 className="text-muted">No photos found</h5>
              <p className="text-muted">Start by adding your first photo for the {pageLabels[activeTab].toLowerCase()}</p>
              <button
                className="btn btn-primary"
                onClick={() => setUploadModalOpen(true)}
              >
                <i className="fas fa-plus me-2"></i>
                Add First Photo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-upload me-2"></i>
                  Upload Photo for {pageLabels[activeTab]}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setUploadModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <PhotoUploadForm
                  onUpload={handlePhotoUpload}
                  onCancel={() => {
                    setUploadModalOpen(false);
                    setPrefilledKey(null);
                  }}
                  suggestedKeys={photoKeyHints[activeTab]}
                  pageName={activeTab}
                  prefilledKey={prefilledKey}
                  photoSpecs={photoSpecs[activeTab]}
                />
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default PagePhotos;